import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
	Button,
	Card,
	Col,
	Input,
	Modal,
	Row,
	Space,
	Spin,
	Table,
	Tag,
	Typography,
	Upload,
	message,
} from "antd";
import {
	InboxOutlined,
	DeleteOutlined,
	DownloadOutlined,
	UploadOutlined,
	ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
	delete_client_marketing,
	get_marketing_assets,
	upload_client_marketing,
} from "../../../../../api/endpoints/clients";
import {
	delete_product_marketing,
	upload_product_marketing,
} from "../../../../../api/endpoints/products";
import Main from "../../../../layout/main/Main";
import {
	AntdTable,
	Container,
	Section,
	TableContainer,
	UploadFile,
} from "./styles";
import Image from "../../../../common/image/Image";
import { FaImages } from "react-icons/fa";
import { COLORS, Typo } from "../../../../layout/theme";
import { CONSTANTS } from "../../../../../utils/constants";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";

const { Dragger } = Upload;

function openUrl(url) {
	window.open(url, "_blank", "noopener,noreferrer");
}

export default function LaboratoryMarketingAssets() {
	const { id: clientId } = useParams();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [loading, setLoading] = useState(false);
	const [flag, setFlag] = useState(false);

	const [data, setData] = useState(null);
	const [q, setQ] = useState("");

	const [isLoading, setIsLoading] = useState(false);

	const fetchAll = async () => {
		if (!clientId) return;
		setLoading(true);
		try {
			const res = await get_marketing_assets(clientId);
			setData(res);
		} catch (e) {
			console.error(e);
			message.error("No se pudo cargar el material de marketing.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientId, flag]);

	const products = useMemo(() => {
		if (!data?.products) return [];
		const term = (q || "").trim().toLowerCase();
		if (!term) return data.products;

		return data.products.filter(
			(p) =>
				(p.title || "").toLowerCase().includes(term) ||
				(p.ean13 || "").toLowerCase().includes(term),
		);
	}, [data, q]);

	const confirmDelete = (title, onOk) => {
		Modal.confirm({
			title,
			icon: <ExclamationCircleOutlined style={{ color: COLORS.Warning }} />,
			okText: "Eliminar",
			okType: "danger",
			cancelText: "Cancelar",
			onOk,
		});
	};

	const columns = useMemo(
		() => [
			{
				title: (algo) => {
					return (
						<div
							style={{
								color: COLORS.White,
								fontWeight: 700,
								fontSize: "14px",
							}}
						>
							<FaImages />
						</div>
					);
				},
				dataIndex: "photoUrl",
				key: "photoUrl",
				width: 100,
				align: "center",
				render: (value, record, index) => (
					<div key={`${value}`}>
						<Image width={40} height={40} src={value} alt={record.title} />
					</div>
				),
			},
			{
				title: (
					<div
						style={{
							color: COLORS.White,
							fontWeight: 700,
							fontSize: "14px",
						}}
					>
						PRODUCTO
					</div>
				),
				dataIndex: "title",
				key: "title",
				render: (v, r) => (
					<Space direction="vertical" size={0}>
						<Container>
							<Typography.Text strong>{v}</Typography.Text>
							<Typography.Text style={{ paddingLeft: 8 }}>
								{r.presentationName}
							</Typography.Text>
						</Container>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							EAN: {r.ean13 || "-"}
						</Typography.Text>
					</Space>
				),
			},
			{
				title: (
					<div
						style={{
							color: COLORS.White,
							fontWeight: 700,
							fontSize: "14px",
						}}
					>
						MATERIAL
					</div>
				),
				dataIndex: "publicUrl",
				key: "publicUrl",
				width: 140,
				render: (url) =>
					url ? (
						<Tag color="green">CARGADO</Tag>
					) : (
						<Tag color="default">VACÍO</Tag>
					),
			},
			{
				title: (
					<div
						style={{
							color: COLORS.White,
							fontWeight: 700,
							fontSize: "14px",
							textAlign: "center",
						}}
					>
						ACCIONES
					</div>
				),
				key: "actions",
				width: 360,
				render: (_, r) => {
					return (
						<Space>
							<Upload
								multiple={false}
								showUploadList={false}
								beforeUpload={() => false}
								action={null}
								onChange={(e) => handleChange(e, "productImport", r.id)}
								disabled={isLoading}
							>
								<Button
									icon={<UploadOutlined />}
									loading={isLoading}
									style={{ borderRadius: 12 }}
								>
									Subir
								</Button>
							</Upload>

							<Button
								icon={<DownloadOutlined />}
								disabled={!r.publicUrl}
								onClick={() => openUrl(r.publicUrl)}
								style={{ borderRadius: 12 }}
							>
								Descargar
							</Button>

							<Button
								danger
								icon={<DeleteOutlined />}
								disabled={!r.publicUrl}
								style={{ borderRadius: 12 }}
								onClick={() =>
									confirmDelete(
										`¿Eliminar material del producto "${r.title}"?`,
										async () => {
											try {
												setIsLoading(true);
												await delete_product_marketing(r.id);
												message.success("Material eliminado.");
												await fetchAll();
											} catch (e) {
												console.error(e);
												message.error("No se pudo eliminar el material.");
											} finally {
												setIsLoading(false);
											}
										},
									)
								}
							>
								Eliminar
							</Button>
						</Space>
					);
				},
			},
		],
		[clientId, flag],
	);

	const handleChange = async (e, type, id) => {
		if (type === "import") {
			setIsLoading(true);
			try {
				const formData = new FormData();
				formData.append(
					"file",
					e.fileList[e.fileList.length - 1].originFileObj,
				);
				formData.append(
					"data",
					JSON.stringify({
						clientId: id,
					}),
				);
				const res = await upload_client_marketing(id, formData);
				if (res?.errors?.length > 0) {
					openNotificationWithIcon(
						TYPE.ERROR,
						"No se pudo subir el archivo",
						"Verifica la extension e integridad del archivo.",
					);
				}
				setFlag((prev) => !prev);
			} catch (error) {
				const errors = Object.entries(error?.response?.data?.errors);
				openNotificationWithIcon(
					TYPE.ERROR,
					errors?.map((e) => `${e[1]} `).concat() ||
						"Verifique que este bien su archivo",
				);
			} finally {
				setIsLoading(false);
			}
		}

		if (type === "productImport") {
			setIsLoading(true);
			try {
				const formData = new FormData();
				formData.append(
					"file",
					e.fileList[e.fileList.length - 1].originFileObj,
				);
				formData.append(
					"data",
					JSON.stringify({
						productId: id,
					}),
				);
				const res = await upload_product_marketing(id, formData);
				if (res?.errors?.length > 0) {
					openNotificationWithIcon(
						TYPE.ERROR,
						"No se pudo subir el archivo",
						"Verifica la extension e integridad del archivo.",
					);
				}
				setFlag((prev) => !prev);
			} catch (error) {
				const errors = Object.entries(error?.response?.data?.errors);
				openNotificationWithIcon(
					TYPE.ERROR,
					errors?.map((e) => `${e[1]} `).concat() ||
						"Verifique que este bien su archivo",
				);
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Material de marketing
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Row gutter={10}>
					<Col span={24}>
						<Card
							loading={loading || !data}
							title={
								<Space direction="vertical" size={0}>
									<Typography.Text strong style={{ fontSize: 21 }}>
										{data?.clientName || "Cliente"}
									</Typography.Text>
									<Typography.Text type="secondary">
										Material general del laboratorio
									</Typography.Text>
								</Space>
							}
							extra={
								<Space>
									<Button
										icon={<DownloadOutlined />}
										disabled={!data?.clientPublicUrl}
										onClick={() => openUrl(data.clientPublicUrl)}
										style={{ borderRadius: 12 }}
									>
										Descargar
									</Button>

									<Button
										danger
										icon={<DeleteOutlined />}
										disabled={!data?.clientPublicUrl}
										style={{ borderRadius: 12 }}
										onClick={() =>
											confirmDelete(
												"¿Eliminar material del cliente?",
												async () => {
													try {
														setIsLoading(true);
														await delete_client_marketing(clientId);
														message.success("Material del cliente eliminado.");
														await fetchAll();
													} catch (e) {
														console.error(e);
														message.error(
															"No se pudo eliminar el material del cliente.",
														);
													} finally {
														setIsLoading(false);
													}
												},
											)
										}
									>
										Eliminar
									</Button>
								</Space>
							}
						>
							<Row gutter={16}>
								<Col md={12} span={24}>
									<UploadFile
										multiple={false}
										showUploadList={false}
										beforeUpload={() => false}
										action={null}
										onChange={(e) => handleChange(e, "import", clientId)}
										disabled={isLoading}
									>
										{isLoading ? (
											<Spin size="default" />
										) : (
											<Section>
												<Image src={CONSTANTS.URL_SHEET_ICON} width="80px" />
												<Typo level={7}>
													Haga click para elegir el archivo desde el explorador
												</Typo>
												<Typo level={7}>
													También puede arrastrarlo y soltarlo en este espacio
												</Typo>
												<Typo level={7}>
													Se mantiene 1 archivo (se reemplaza el anterior al
													subir uno nuevo).
												</Typo>
											</Section>
										)}
									</UploadFile>
								</Col>

								<Col md={12} span={24}>
									<Space direction="vertical" style={{ width: "100%" }}>
										<Typography.Text
											style={{ color: COLORS.Primary, fontWeight: 600 }}
										>
											Estado actual
										</Typography.Text>

										{data?.clientPublicUrl ? (
											<Space direction="vertical" size={4}>
												<Tag color="green">CARGADO</Tag>
												<Typography.Text
													type="secondary"
													style={{ wordBreak: "break-all" }}
												>
													{data.clientPublicUrl}
												</Typography.Text>
											</Space>
										) : (
											<Tag color="default">SIN MATERIAL</Tag>
										)}
									</Space>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
				<Row gutter={10}>
					<Col span={24}>
						<Card
							loading={loading || !data}
							title="Material por producto"
							extra={
								<Input
									placeholder="Buscar por nombre o EAN..."
									value={q}
									onChange={(e) => setQ(e.target.value)}
									style={{ width: 320, borderRadius: 15 }}
									allowClear
								/>
							}
						>
							<TableContainer
								containerMarginTop={"0px"}
								containerBackground={COLORS.White}
							>
								<AntdTable
									rowKey="id"
									dataSource={products}
									columns={columns}
									pagination={{ pageSize: 10, showSizeChanger: true }}
								/>
							</TableContainer>
						</Card>
					</Col>
				</Row>
			</Main.Body>
		</Main>
	);
}
